import { SelectableItem } from "../../../processingActivity/ProcessingActivity.types";
import { RiskAreaViewModel } from "../../../riskSettings/riskArea/riskAreas/RiskAreas.types";
import { getRiskLevelIndicatorChipStyle } from "../../../riskSettings/riskArea/riskAreaDialog/riskLevels/riskLevelIndicators";
import { CurrentAssessmentCardModel, RiskAssessmentViewModel } from "./AssessDialog.types";
import { PolicyControl } from "../../../policy/policySections/PolicySections.types.ts";
import { resolveControls } from "../../../policy/policySections/PolicySections.utils";

// Turns a risk area's ordered level labels into score dropdown options ("1 - <label>"); the option id is the
// 1-based score. Shared by the single-entity assess dialog and the bulk-assess dialog.
export const toScoreOption = (levels: Array<string>) => levels.map((label, index) => ({ id: (index + 1).toString(), name: `${index + 1} - ${label}` }));

// Ids whose master data was deleted since the revision was scored no longer resolve to an option, so they are
// silently dropped from the reference card.
const resolveSelectableNames = (ids: Array<string>, options: Array<SelectableItem>) =>
    ids.map((id) => options.find((option) => option.id === id)?.name).filter((name): name is string => name !== undefined);

// Builds the read-only "current assessment" card purely from the prefill (the current revision) and the risk
// area, so every rating value — score, level label and risk chip — stays paired with its own justification. The
// grid row can lag the freshly-fetched revision, so only the who/when metadata and the assessment's note, which the
// prefill does not carry, are taken from it. Coverage is the prefill's own, i.e. what is stored — the dialog stages its edits separately
// and only commits them by scoring, so the card keeps reporting the assessment as saved.
// Returns null until the head is scored and both sources have loaded.
export function buildCurrentAssessmentCard(
    prefill: RiskAssessmentViewModel | undefined,
    riskArea: RiskAreaViewModel | undefined,
    consequenceTypeOptions: Array<SelectableItem>,
    measureOptions: Array<SelectableItem>,
    assessedByName: string,
    assessmentDateLabel: string,
    note: string | null,
    exposureConfigured: boolean,
    exposureLabel: string,
    exposureValueText: string | null,
    availableControls: Array<PolicyControl>
): CurrentAssessmentCardModel | null {
    if (prefill === undefined || riskArea === undefined || prefill.consequence == null) return null;

    const risk = prefill.probability != null ? riskArea.matrix[prefill.consequence - 1][prefill.probability - 1] : null;

    return {
        consequence: prefill.consequence,
        consequenceLabel: riskArea.consequenceLevels[prefill.consequence - 1],
        probability: prefill.probability,
        probabilityLabel: prefill.probability != null ? riskArea.probabilityLevels[prefill.probability - 1] : "",
        risk,
        riskLabel: risk != null ? riskArea.riskLevels[risk - 1] : "",
        riskStyle: risk != null ? getRiskLevelIndicatorChipStyle(risk, riskArea.riskLevelIndicators) : null,
        consequenceJustification: prefill.consequenceJustification ?? "",
        probabilityJustification: prefill.probabilityJustification ?? "",
        consequenceTypeNames: resolveSelectableNames(prefill.consequenceTypeIds, consequenceTypeOptions),
        measureNames: resolveSelectableNames(prefill.measureIds, measureOptions),
        assessedByName,
        participantNames: prefill.participants.map((participant) => participant.name),
        assessmentDateLabel,
        reviewNote: prefill.reviewNote ?? "",
        note: note ?? "",
        exposureConfigured,
        exposureLabel,
        exposureValueText,
        confidence: prefill.confidence,
        linkedControls: resolveControls(prefill.frameworkControlIds, availableControls),
    };
}
